import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import { useRef, useState } from 'react';
import styled from 'styled-components';

export interface CollectionPickerOption {
  id: string;
  name: string;
  description: string;
}

interface Props {
  options: CollectionPickerOption[];
  selectedId: string;
  onSelect: (id: string) => void;
}

/**
 * A styled collection dropdown whose options show the collection name with its
 * description underneath (a native `<select>` can't). Shared by the narrow-screen
 * collection list and the import modal so both read identically.
 */
const CollectionPicker = ({ options, selectedId, onSelect }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideAlerter(ref, () => setOpen(false));
  const selected = options.find(o => o.id === selectedId) ?? options[0];

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className="text">
          <span className="name">{selected?.name}</span>
          {selected?.description && (
            <span className="desc">{selected.description}</span>
          )}
        </span>
        <Chevron aria-hidden>▾</Chevron>
      </Trigger>
      {open && (
        <List role="listbox">
          {options.map(o => (
            <Option
              key={o.id}
              type="button"
              role="option"
              aria-selected={o.id === selectedId}
              $selected={o.id === selectedId}
              onClick={() => {
                onSelect(o.id);
                setOpen(false);
              }}
            >
              <span className="name">{o.name}</span>
              {o.description && <span className="desc">{o.description}</span>}
            </Option>
          ))}
        </List>
      )}
    </Wrap>
  );
};

export default CollectionPicker;

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: var(--primary);
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .name {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
  }

  .desc {
    font-size: var(--text-xs);
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Chevron = styled.span`
  flex-shrink: 0;
  color: var(--text-secondary);
  font-size: var(--text-xs);
`;

const List = styled.div`
  position: absolute;
  z-index: 100001;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 320px;
  overflow: auto;
  background: var(--surface-elevated, var(--surface));
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.3));
`;

const Option = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px 12px;
  background: ${p =>
    p.$selected
      ? 'color-mix(in srgb, var(--primary) 12%, transparent)'
      : 'none'};
  border: none;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  text-align: left;
  ${p => p.$selected && 'box-shadow: inset 2px 0 0 var(--primary);'}

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: color-mix(in srgb, var(--primary) 8%, transparent);
  }

  .name {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
  }

  .desc {
    font-size: var(--text-xs);
    color: var(--text-muted);
    overflow-wrap: anywhere;
  }
`;
